import { useState, useEffect } from "react";
import {
  getMyRestaurant,
  createRestaurant,
  updateRestaurant,
  toggleRestaurantStatus,
} from "../services/restaurantService";

const EMPTY_FORM = {
  name: "",
  description: "",
  cuisineType: "",
  openingHours: "",
  images: "",
  contactEmail: "",
  contactMobile: "",
  contactInstagram: "",
  contactTwitter: "",
  street: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
};

export default function RestaurantManagementPage() {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [showForm, setShowForm]     = useState(false);
  const [isEditing, setIsEditing]   = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/"; return; }
    loadRestaurant();
  }, []);

  const loadRestaurant = async () => {
    setLoading(true);
    try {
      const rest = await getMyRestaurant();
      setRestaurant(rest);
    } catch (e) {
      // No restaurant yet — show create form
      setRestaurant(null);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const openEditForm = () => {
    if (!restaurant) return;
    setForm({
      name: restaurant.name || "",
      description: restaurant.description || "",
      cuisineType: restaurant.cuisineType || "",
      openingHours: restaurant.openingHours || "",
      images: restaurant.images?.[0] || "",
      contactEmail: restaurant.contactInformation?.email || "",
      contactMobile: restaurant.contactInformation?.mobile || "",
      contactInstagram: restaurant.contactInformation?.instagram || "",
      contactTwitter: restaurant.contactInformation?.twitter || "",
      street: restaurant.address?.street || "",
      city: restaurant.address?.city || "",
      state: restaurant.address?.state || "",
      pincode: restaurant.address?.pincode || "",
      country: restaurant.address?.country || "India",
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        description: form.description,
        cuisineType: form.cuisineType,
        openingHours: form.openingHours,
        images: form.images ? [form.images] : [],
        contactInformation: {
          email: form.contactEmail,
          mobile: form.contactMobile,
          instagram: form.contactInstagram,
          twitter: form.contactTwitter,
        },
        address: {
          street: form.street,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          country: form.country,
        },
      };

      let updated;
      if (isEditing) {
        updated = await updateRestaurant(restaurant.id, payload);
      } else {
        updated = await createRestaurant(payload);
      }
      setRestaurant(updated);
      setShowForm(false);
      setIsEditing(false);
      setForm(EMPTY_FORM);
      showSuccessMsg(isEditing ? "Restaurant update ho gaya! ✅" : "Restaurant ban gaya! 🎉");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const updated = await toggleRestaurantStatus(restaurant.id);
      setRestaurant(updated);
      showSuccessMsg(updated.open ? "Restaurant ab OPEN hai! 🟢" : "Restaurant ab CLOSED hai! 🔴");
    } catch (e) { setError(e.message); }
  };

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">

      {/* Navbar */}
      <nav className="bg-[#1a1a1a] border-b border-zinc-800 px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🍽️</span>
          <span className="text-xl font-black">Sav<span className="text-orange-500">ora</span></span>
          <span className="text-zinc-600 mx-2">|</span>
          <span className="text-zinc-400 text-sm">Restaurant</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => window.location.href = "/dashboard"}
            className="text-xs text-zinc-400 hover:text-white transition-colors">
            ← Dashboard
          </button>
          <button onClick={() => { localStorage.clear(); window.location.href = "/"; }}
            className="text-xs text-zinc-500 hover:text-red-400 transition-colors">
            Logout →
          </button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Alerts */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-red-400 text-sm">⚠️ {error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-green-400 text-sm">{success}</p>
          </div>
        )}

        {/* Restaurant Card — show if exists and not editing */}
        {restaurant && !showForm && (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl overflow-hidden mb-6">
            {restaurant.images?.[0] && (
              <img src={restaurant.images[0]} alt={restaurant.name}
                className="w-full h-52 object-cover" />
            )}
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-black text-white">{restaurant.name}</h1>
                  <p className="text-zinc-500 text-sm mt-1">{restaurant.description}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border shrink-0 ml-4 ${
                  restaurant.open
                    ? "bg-green-500/10 text-green-400 border-green-500/30"
                    : "bg-red-500/10 text-red-400 border-red-500/30"
                }`}>
                  {restaurant.open ? "● OPEN" : "● CLOSED"}
                </span>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: "Cuisine", value: restaurant.cuisineType },
                  { label: "Hours", value: restaurant.openingHours },
                  { label: "City", value: restaurant.address?.city },
                  { label: "Mobile", value: restaurant.contactInformation?.mobile },
                  { label: "Email", value: restaurant.contactInformation?.email },
                  { label: "Instagram", value: restaurant.contactInformation?.instagram },
                ].map(item => item.value && (
                  <div key={item.label} className="bg-zinc-900 rounded-xl px-4 py-3">
                    <p className="text-zinc-500 text-xs mb-0.5">{item.label}</p>
                    <p className="text-zinc-200 text-sm font-medium">{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button onClick={handleToggle}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    restaurant.open
                      ? "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
                      : "bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30"
                  }`}>
                  {restaurant.open ? "🔴 Close Restaurant" : "🟢 Open Restaurant"}
                </button>
                <button onClick={openEditForm}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white transition-colors">
                  ✏️ Edit Restaurant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No Restaurant — show create prompt */}
        {!restaurant && !showForm && (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-16 text-center">
            <div className="text-6xl mb-4">🏪</div>
            <h2 className="text-xl font-bold text-white mb-2">Koi Restaurant Nahi</h2>
            <p className="text-zinc-500 text-sm mb-6">Apna pehla restaurant create karo!</p>
            <button onClick={() => setShowForm(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors">
              + Create Restaurant
            </button>
          </div>
        )}

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <h2 className="text-lg font-black text-white">
                {isEditing ? "✏️ Edit Restaurant" : "🏪 Create Restaurant"}
              </h2>
              {restaurant && (
                <button onClick={() => { setShowForm(false); setIsEditing(false); }}
                  className="text-zinc-500 hover:text-white text-xl">✕</button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">

              {/* Basic Info */}
              <div>
                <h3 className="text-xs text-orange-400 uppercase tracking-widest mb-3">Basic Info</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Restaurant Name *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} required
                      placeholder="Spice Garden"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                      placeholder="Best Indian food in town..."
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Cuisine Type</label>
                      <input type="text" name="cuisineType" value={form.cuisineType} onChange={handleChange}
                        placeholder="Indian, Chinese..."
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Opening Hours</label>
                      <input type="text" name="openingHours" value={form.openingHours} onChange={handleChange}
                        placeholder="9 AM - 11 PM"
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Image URL</label>
                    <input type="url" name="images" value={form.images} onChange={handleChange}
                      placeholder="https://images.pexels.com/..."
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-xs text-orange-400 uppercase tracking-widest mb-3">Address</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Street *</label>
                    <input type="text" name="street" value={form.street} onChange={handleChange} required
                      placeholder="123 Main Street"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">City *</label>
                      <input type="text" name="city" value={form.city} onChange={handleChange} required
                        placeholder="Mumbai"
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">State</label>
                      <input type="text" name="state" value={form.state} onChange={handleChange}
                        placeholder="Maharashtra"
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Pincode</label>
                      <input type="text" name="pincode" value={form.pincode} onChange={handleChange}
                        placeholder="400001"
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Country</label>
                      <input type="text" name="country" value={form.country} onChange={handleChange}
                        className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-xs text-orange-400 uppercase tracking-widest mb-3">Contact Info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Email</label>
                    <input type="email" name="contactEmail" value={form.contactEmail} onChange={handleChange}
                      placeholder="owner@restaurant.com"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Mobile</label>
                    <input type="text" name="contactMobile" value={form.contactMobile} onChange={handleChange}
                      placeholder="9876543210"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Instagram</label>
                    <input type="text" name="contactInstagram" value={form.contactInstagram} onChange={handleChange}
                      placeholder="@restaurant"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Twitter</label>
                    <input type="text" name="contactTwitter" value={form.contactTwitter} onChange={handleChange}
                      placeholder="@restaurant"
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl px-4 py-3 text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500 transition-colors" />
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <p className="text-red-400 text-sm">⚠️ {error}</p>
                </div>
              )}

              <button type="submit" disabled={saving}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors text-sm">
                {saving ? "Saving..." : isEditing ? "Update Restaurant →" : "Create Restaurant →"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}